import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useGuardedRealtimeList from '../../../realtime/useGuardedRealtimeList';
import { Accordion, Alert, Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../../axios/axiosConfig";
import useActivityLogChangeLog from "../../../functions/useActivityLogChangeLog";
import { fetchData } from "../../../redux/slices/apiCallSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectUserDropdowns } from "../../../redux/slices/dropdownSlice";
import { formatDateCompact, formatDateForAPI, formatDateForTable } from "../../../utils/dateUtils";
import "./TaskManagerDashboard.css";

const MANAGER_COLORS = [
  "#1e88e5",
  "#172a9a",
  "#e96a32",
  "#7f0c8d",
  "#dd4aa5",
  "#7653c7",
  "#d3b900",
  "#13847f",
  "#23b4d8",
  "#d8494f",
  "#25a84a",
  "#f39a55",
];

const PIE_COLORS = [
  "#1e88e5",
  "#172a9a",
  "#e96a32",
  "#7f0c8d",
  "#dd4aa5",
  "#7653c7",
  "#d3b900",
  "#13847f",
  "#25a84a",
  "#23b4d8",
  "#d8494f",
  "#f39a55",
  "#a66dd4",
  "#7a8794",
];

const chartMargins = {
  top: 28,
  right: 20,
  bottom: 56,
  left: 68,
};

const COMPACT_SUMMARY_MEDIA_QUERY =
  "(max-width: 767.98px), (min-width: 768px) and (max-width: 1366px) and (hover: none) and (pointer: coarse)";

const normalizeKeyPart = (value) => String(value || "").trim().toLowerCase();

const buildHoursKey = (productCode, requestType) =>
  `${normalizeKeyPart(productCode)}::${normalizeKeyPart(requestType)}`;

const isIncompleteActivityItem = (item) => {
  const status = normalizeKeyPart(item.Status);
  return !item.DateCompleted && status !== "complete";
};

const formatNumber = (value) => {
  const rounded = Math.round((Number(value) || 0) * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const getDueDateBucket = (dueDateValue) => {
  if (!dueDateValue) return null;
  const dueDateKey = formatDateForAPI(dueDateValue);
  if (!dueDateKey) return null;

  return {
    key: dueDateKey,
    label: formatDateCompact(dueDateValue),
    year: dueDateKey.slice(0, 4),
  };
};

const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeArc = (centerX, centerY, radius, startAngle, endAngle) => {
  const start = polarToCartesian(centerX, centerY, radius, endAngle);
  const end = polarToCartesian(centerX, centerY, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return [
    `M ${centerX} ${centerY}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
};

const createPieData = (records, fieldName, colorResolver) => {
  const grouped = new Map();
  records.forEach((record) => {
    const label = record[fieldName] || "Unassigned";
    grouped.set(label, (grouped.get(label) || 0) + record.EstimatedHours);
  });

  const total = Array.from(grouped.values()).reduce((sum, value) => sum + value, 0);
  let currentAngle = 0;

  return Array.from(grouped.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label, value], index) => {
      const angle = total ? (value / total) * 360 : 0;
      const slice = {
        label,
        value,
        percent: total ? (value / total) * 100 : 0,
        startAngle: currentAngle,
        endAngle: currentAngle + angle,
        color: colorResolver?.(label, index) || PIE_COLORS[index % PIE_COLORS.length],
      };
      currentAngle += angle;
      return slice;
    });
};

const PieChart = ({ title, data, selectedLabel, onSelect }) => {
  const size = 350;
  const radius = 130;
  const center = size / 2;
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <section className="task-manager-pie-panel">
      <div className="task-manager-chart-title">{title}</div>
      {data.length === 0 ? (
        <div className="task-manager-empty compact">No data</div>
      ) : (
        <div className="task-manager-pie-content">
          <svg className="task-manager-pie-chart" viewBox={`0 0 ${size} ${size}`} role="img" aria-label={title}>
            {data.map((slice) => {
              const isSelected = selectedLabel === slice.label;
              const dimmed = selectedLabel && !isSelected;
              const midAngle = (slice.startAngle + slice.endAngle) / 2;
              const labelPoint = polarToCartesian(center, center, radius + 28, midAngle);
              const labelAnchor = labelPoint.x >= center ? "start" : "end";

              return (
                <g key={slice.label}>
                  <path
                    d={describeArc(center, center, radius, slice.startAngle, slice.endAngle)}
                    fill={slice.color}
                    className={`pie-slice ${isSelected ? "selected" : ""} ${dimmed ? "dimmed" : ""}`}
                    role="button"
                    tabIndex="0"
                    aria-label={`${slice.label}, ${formatNumber(slice.value)} estimated hours, ${formatNumber(slice.percent)} percent`}
                    onClick={() => onSelect(slice.label)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSelect(slice.label);
                      }
                    }}
                  >
                    <title>{`${slice.label}: ${formatNumber(slice.value)} hours (${formatNumber(slice.percent)}%)`}</title>
                  </path>
                  {slice.percent >= 4 && (
                    <text
                      x={labelPoint.x}
                      y={labelPoint.y}
                      textAnchor={labelAnchor}
                      className={`pie-label ${dimmed ? "dimmed" : ""}`}
                    >
                      {formatNumber(slice.value)}
                    </text>
                  )}
                </g>
              );
            })}
            <circle cx={center} cy={center} r="48" className="pie-center" />
            <text x={center} y={center - 3} textAnchor="middle" className="pie-total-label">Total</text>
            <text x={center} y={center + 17} textAnchor="middle" className="pie-total-value">{formatNumber(total)}</text>
          </svg>
          <div className="task-manager-pie-legend">
            {data.map((slice) => (
              <button
                type="button"
                key={slice.label}
                className={`legend-item pie-legend-item ${selectedLabel === slice.label ? "active" : ""}`}
                onClick={() => onSelect(slice.label)}
              >
                <span style={{ backgroundColor: slice.color }} />
                <span className="pie-legend-label">{slice.label}</span>
                <span className="pie-legend-value">{formatNumber(slice.value)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const TaskManagerDashboard = () => {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const userDropdowns = useSelector(selectUserDropdowns);
  const { logActivityLogChange } = useActivityLogChangeLog();
  const chartContainerRef = useRef(null);
  const [activityLog, setActivityLog] = useState([]);
  const [activityLogHours, setActivityLogHours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [savingProjectManagerId, setSavingProjectManagerId] = useState(null);
  useGuardedRealtimeList({ apiKey: 'activityLog', apply: rows => setActivityLog(rows.filter(isIncompleteActivityItem)), blocked: loading || Boolean(savingProjectManagerId) });
  useGuardedRealtimeList({ apiKey: 'activityLogHours', apply: setActivityLogHours, blocked: loading || Boolean(savingProjectManagerId) });
  const [chartContainerWidth, setChartContainerWidth] = useState(0);
  const [selectedBar, setSelectedBar] = useState(null);
  const [selectedRsm, setSelectedRsm] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [summaryPanelOpen, setSummaryPanelOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return !window.matchMedia(COMPACT_SUMMARY_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(COMPACT_SUMMARY_MEDIA_QUERY);
    const handleBreakpointChange = (event) => setSummaryPanelOpen(!event.matches);

    mediaQuery.addEventListener("change", handleBreakpointChange);
    return () => mediaQuery.removeEventListener("change", handleBreakpointChange);
  }, []);

  const projectManagerOptions = useMemo(() => [
    ...(userDropdowns?.appsOptions || []),
    ...(userDropdowns?.adminOptions || []),
  ], [userDropdowns]);

  const projectManagerOptionValues = useMemo(() => {
    const seen = new Set();
    return projectManagerOptions
      .map((option) => option.displayName || option.value || "")
      .filter((value) => {
        if (!value || seen.has(value)) return false;
        seen.add(value);
        return true;
      });
  }, [projectManagerOptions]);

  const fetchDashboardData = useCallback(async () => {
    if (!apiUrls.activityLog || !apiUrls.activityLogHours) return;

    setLoading(true);
    setError("");

    try {
      const [activityResponse, hoursResponse] = await Promise.all([
        dispatch(fetchData({ url: apiUrls.activityLog })).unwrap(),
        dispatch(fetchData({ url: apiUrls.activityLogHours })).unwrap(),
      ]);

      const incompleteActivityLog = Array.isArray(activityResponse)
        ? activityResponse.filter(isIncompleteActivityItem)
        : [];

      setActivityLog(incompleteActivityLog);
      setActivityLogHours(Array.isArray(hoursResponse) ? hoursResponse : []);
    } catch (err) {
      console.error("Error fetching task manager dashboard data:", err);
      setError("Failed to load Task Manager dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [apiUrls.activityLog, apiUrls.activityLogHours, dispatch]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return undefined;

    const updateChartWidth = () => {
      setChartContainerWidth(Math.floor(container.clientWidth || 0));
    };

    updateChartWidth();

    if (!window.ResizeObserver) {
      window.addEventListener("resize", updateChartWidth);
      return () => window.removeEventListener("resize", updateChartWidth);
    }

    const resizeObserver = new ResizeObserver(updateChartWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  const estimatedHoursByLookup = useMemo(() => {
    const lookup = new Map();

    activityLogHours.forEach((hoursItem) => {
      const key = buildHoursKey(hoursItem.ProductCode, hoursItem.RequestType);
      lookup.set(key, Number(hoursItem.AvgHours) || 0);
    });

    return lookup;
  }, [activityLogHours]);

  const estimatedRecords = useMemo(() => {
    const records = [];
    let unmatchedCount = 0;

    activityLog.forEach((item) => {
      const hoursKey = buildHoursKey(item.ProductCode, item.RequestType);
      const estimatedHours = estimatedHoursByLookup.get(hoursKey);
      if (!estimatedHours) {
        unmatchedCount += 1;
        return;
      }

      const dueDateBucket = getDueDateBucket(item.DueDate);

      records.push({
        ...item,
        EstimatedHours: estimatedHours,
        ProjectManager: item.ProjectManager || "Unassigned",
        ProjectManagerValue: item.ProjectManager || "",
        RSM: item.RSM || "Unassigned",
        StatusLabel: item.Status || "No Status",
        DueDateKey: dueDateBucket?.key || null,
        DueDateLabel: dueDateBucket?.label || "No Due Date",
        DueDateYear: dueDateBucket?.year || null,
      });
    });

    return { records, unmatchedCount };
  }, [activityLog, estimatedHoursByLookup]);

  const statusSummaries = useMemo(() => {
    const grouped = new Map();

    estimatedRecords.records.forEach((record) => {
      const status = record.StatusLabel || "No Status";
      const current = grouped.get(status) || { status, count: 0, hours: 0 };
      current.count += 1;
      current.hours += record.EstimatedHours;
      grouped.set(status, current);
    });

    return Array.from(grouped.values()).sort((a, b) => a.status.localeCompare(b.status));
  }, [estimatedRecords.records]);

  const statusFilteredRecords = useMemo(() => {
    if (!selectedStatus) return estimatedRecords.records;
    return estimatedRecords.records.filter((record) => record.StatusLabel === selectedStatus);
  }, [estimatedRecords.records, selectedStatus]);

  const barRecords = useMemo(() => {
    return statusFilteredRecords.filter((record) => {
      if (selectedRsm && record.RSM !== selectedRsm) return false;
      return true;
    });
  }, [statusFilteredRecords, selectedRsm]);

  const chartData = useMemo(() => {
    const managers = new Set();
    const dateMap = new Map();

    barRecords.forEach((record) => {
      if (!record.DueDateKey) return;

      const projectManager = record.ProjectManager;
      const dueDateKey = record.DueDateKey;

      if (!dateMap.has(dueDateKey)) {
        dateMap.set(dueDateKey, {
          key: dueDateKey,
          year: record.DueDateYear,
          label: record.DueDateLabel,
          managers: {},
          total: 0,
        });
      }

      const dateBucket = dateMap.get(dueDateKey);
      dateBucket.managers[projectManager] = (dateBucket.managers[projectManager] || 0) + record.EstimatedHours;
      dateBucket.total += record.EstimatedHours;
      managers.add(projectManager);
    });

    const dueDates = Array.from(dateMap.values()).sort((a, b) => a.key.localeCompare(b.key));
    const managerList = Array.from(managers).sort((a, b) => a.localeCompare(b));

    return {
      dueDates,
      managers: managerList,
      unmatchedCount: estimatedRecords.unmatchedCount,
      totalHours: dueDates.reduce((sum, dateBucket) => sum + dateBucket.total, 0),
    };
  }, [barRecords, estimatedRecords.unmatchedCount]);

  const allProjectManagers = useMemo(() => {
    return Array.from(new Set(
      estimatedRecords.records.map((record) => record.ProjectManager || "Unassigned")
    )).sort((a, b) => a.localeCompare(b));
  }, [estimatedRecords.records]);

  const pieRecords = useMemo(() => {
    return statusFilteredRecords.filter((record) => {
      if (selectedBar?.dueDateKey && record.DueDateKey !== selectedBar.dueDateKey) return false;
      if (selectedBar?.projectManager && record.ProjectManager !== selectedBar.projectManager) return false;
      if (selectedRsm && record.RSM !== selectedRsm) return false;
      return true;
    });
  }, [statusFilteredRecords, selectedBar, selectedRsm]);

  const projectManagerPieRecords = useMemo(() => {
    return statusFilteredRecords.filter((record) => {
      if (selectedBar?.dueDateKey && record.DueDateKey !== selectedBar.dueDateKey) return false;
      if (selectedRsm && record.RSM !== selectedRsm) return false;
      return true;
    });
  }, [statusFilteredRecords, selectedBar?.dueDateKey, selectedRsm]);

  const managerColorMap = useMemo(() => {
    const map = new Map();
    allProjectManagers.forEach((manager, index) => {
      map.set(manager, MANAGER_COLORS[index % MANAGER_COLORS.length]);
    });
    return map;
  }, [allProjectManagers]);

  const rsmPieData = useMemo(() => createPieData(pieRecords, "RSM"), [pieRecords]);
  const projectManagerPieData = useMemo(
    () => createPieData(
      projectManagerPieRecords,
      "ProjectManager",
      (manager, index) => managerColorMap.get(manager) || MANAGER_COLORS[index % MANAGER_COLORS.length]
    ),
    [managerColorMap, projectManagerPieRecords]
  );

  const filteredResultRows = useMemo(() => {
    return statusFilteredRecords
      .filter((record) => {
        if (selectedBar?.dueDateKey && record.DueDateKey !== selectedBar.dueDateKey) return false;
        if (selectedBar?.projectManager && record.ProjectManager !== selectedBar.projectManager) return false;
        if (selectedRsm && record.RSM !== selectedRsm) return false;
        return true;
      })
      .sort((a, b) => {
        const managerCompare = a.ProjectManager.localeCompare(b.ProjectManager);
        if (managerCompare !== 0) return managerCompare;
        return (a.ProjectName || "").localeCompare(b.ProjectName || "");
      });
  }, [statusFilteredRecords, selectedBar, selectedRsm]);

  const selectedDetails = useMemo(() => {
    if (!selectedBar) return null;

    const dueDate = chartData.dueDates.find((dateItem) => dateItem.key === selectedBar.dueDateKey);
    if (!dueDate) return null;

    return {
      dueDateLabel: `${dueDate.label} ${dueDate.year}`,
      projectManager: selectedBar.projectManager,
      hours: dueDate.managers[selectedBar.projectManager] || 0,
    };
  }, [chartData.dueDates, selectedBar]);

  const chart = useMemo(() => {
    const { dueDates, managers } = chartData;
    const dataWidth = dueDates.length * Math.max(105, managers.length * 22);
    const width = Math.max(chartContainerWidth || 940, dataWidth, 720);
    const height = 660;
    const plotWidth = width - chartMargins.left - chartMargins.right;
    const plotHeight = height - chartMargins.top - chartMargins.bottom;
    const highestValue = Math.max(
      0,
      ...dueDates.flatMap((dueDate) => managers.map((manager) => dueDate.managers[manager] || 0))
    );
    const chartMax = Math.max(13, highestValue + 5);
    const ticks = Array.from({ length: 6 }, (_, index) => Math.round((chartMax / 5) * index * 10) / 10);
    const eightHourLineY = chartMargins.top + plotHeight - (8 / chartMax) * plotHeight;
    const dateGroupWidth = dueDates.length ? plotWidth / dueDates.length : plotWidth;
    const barGap = 4;
    const groupPadding = Math.min(30, dateGroupWidth * 0.18);
    const availableBarWidth = Math.max(12, dateGroupWidth - groupPadding * 2);
    const barWidth = managers.length ? Math.max(8, Math.min(28, (availableBarWidth - barGap * (managers.length - 1)) / managers.length)) : 0;
    const actualGroupWidth = managers.length * barWidth + Math.max(0, managers.length - 1) * barGap;

    return {
      width,
      height,
      plotWidth,
      plotHeight,
      chartMax,
      ticks,
      eightHourLineY,
      dateGroupWidth,
      groupPadding,
      barGap,
      barWidth,
      actualGroupWidth,
    };
  }, [chartData, chartContainerWidth]);

  const handleBarClick = (dueDateKey, projectManager) => {
    setSelectedBar((current) => {
      if (current?.dueDateKey === dueDateKey && current?.projectManager === projectManager) {
        return null;
      }
      return { dueDateKey, projectManager };
    });
  };

  const handleProjectManagerChange = async (record, newProjectManager) => {
    const originalProjectManager = record.ProjectManagerValue || "";
    if (newProjectManager === originalProjectManager || savingProjectManagerId) return;

    setSavingProjectManagerId(record.ID);
    setSaveMessage("");
    setError("");

    try {
      const updateData = {
        ID: record.ID,
        ProjectManager: newProjectManager || null,
      };

      const response = await axiosInstance.patch(`${apiUrls.activityLog}?id=${record.ID}`, updateData);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`Server responded with status ${response.status}`);
      }

      setActivityLog((currentLogs) => currentLogs.map((log) => (
        log.ID === record.ID
          ? { ...log, ProjectManager: newProjectManager || null }
          : log
      )));

      try {
        await logActivityLogChange(
          record.ID,
          record.ProjectName || "",
          record.JobNumber || "",
          "ProjectManager",
          originalProjectManager,
          newProjectManager || null
        );
      } catch (logError) {
        console.warn("Project Manager update succeeded but change logging failed:", logError);
      }

      setSaveMessage("Project Manager updated.");
    } catch (err) {
      console.error("Error updating Project Manager:", err);
      setError("Failed to update Project Manager.");
    } finally {
      setSavingProjectManagerId(null);
    }
  };

  return (
    <div className="task-manager-dashboard">
      <div className="task-manager-header">
        <div>
          <h1>Task Manager</h1>
          <div className="task-manager-subtitle">
            Estimated hours are matched by Product Code and Request Type.
          </div>
        </div>
        <Button variant="outline-primary" size="sm" onClick={fetchDashboardData} disabled={loading}>
          Refresh
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {saveMessage && <Alert variant="success">{saveMessage}</Alert>}

      {loading ? (
        <div className="task-manager-loading">
          <Spinner animation="border" role="status" />
          <span>Loading Task Manager dashboard...</span>
        </div>
      ) : (
        <>
          <Accordion
            className="task-manager-summary-accordion"
            activeKey={summaryPanelOpen ? "summary-filters" : null}
            onSelect={(eventKey) => setSummaryPanelOpen(eventKey === "summary-filters")}
          >
            <Accordion.Item eventKey="summary-filters">
              <Accordion.Header>
                <span>Dashboard Summary &amp; Filters</span>
                {(selectedStatus || selectedBar || selectedRsm) && (
                  <span className="task-manager-accordion-status">Filters active</span>
                )}
              </Accordion.Header>
              <Accordion.Body>
                <div className="task-manager-summary-row">
                  <div className="task-manager-metric">
                    <span>Total Estimated Hours</span>
                    <strong>{formatNumber(chartData.totalHours)}</strong>
                  </div>
                  <div className="task-manager-metric">
                    <span>Activity Log Items</span>
                    <strong>{activityLog.length}</strong>
                  </div>
                  <div className="task-manager-metric">
                    <span>Unmatched Items</span>
                    <strong>{estimatedRecords.unmatchedCount}</strong>
                  </div>
                  {statusSummaries.map((summary) => (
                    <button
                      type="button"
                      key={summary.status}
                      className={`task-manager-metric status-filter-card ${selectedStatus === summary.status ? "selected" : ""}`}
                      onClick={() => {
                        setSelectedStatus((current) => current === summary.status ? null : summary.status);
                        setSelectedBar(null);
                        setSelectedRsm(null);
                      }}
                    >
                      <span>{summary.status}</span>
                      <strong>{formatNumber(summary.hours)}</strong>
                      <small>{summary.count} items</small>
                    </button>
                  ))}
                  {selectedStatus && (
                    <div className="task-manager-metric selected">
                      <span>Status</span>
                      <strong>{selectedStatus}</strong>
                      <button type="button" onClick={() => setSelectedStatus(null)}>Clear</button>
                    </div>
                  )}
                  {selectedDetails && (
                    <div className="task-manager-metric selected">
                      <span>{selectedDetails.dueDateLabel} / {selectedDetails.projectManager}</span>
                      <strong>{formatNumber(selectedDetails.hours)}</strong>
                      <button type="button" onClick={() => setSelectedBar(null)}>Clear</button>
                    </div>
                  )}
                  {selectedRsm && (
                    <div className="task-manager-metric selected">
                      <span>RSM</span>
                      <strong>{selectedRsm}</strong>
                      <button type="button" onClick={() => setSelectedRsm(null)}>Clear</button>
                    </div>
                  )}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          <div className="task-manager-chart-layout">
            <section className="task-manager-chart-panel">
              <div className="task-manager-chart-title">
                Estimated Hours by Due Date and Project Manager
              </div>

              {chartData.dueDates.length === 0 ? (
                <div className="task-manager-empty">No estimated hour data is available for the Activity Log.</div>
              ) : (
                <>
                  <div className="task-manager-legend" aria-label="Project Manager legend">
                    <span className="task-manager-legend-title">ProjectManager</span>
                    <div className="task-manager-legend-items">
                      {chartData.managers.map((manager) => (
                        <button
                          type="button"
                          key={manager}
                          className={`legend-item ${selectedBar?.projectManager === manager ? "active" : ""}`}
                          onClick={() => setSelectedBar((current) => current?.projectManager === manager ? null : { dueDateKey: null, projectManager: manager })}
                        >
                          <span style={{ backgroundColor: managerColorMap.get(manager) }} />
                          <span className="legend-item-label">{manager}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="task-manager-chart-scroll" ref={chartContainerRef}>
                    <svg
                      className="task-manager-bar-chart"
                      width={chart.width}
                      height={chart.height}
                      viewBox={`0 0 ${chart.width} ${chart.height}`}
                      role="img"
                      aria-label="Estimated hours by due date and project manager"
                    >
                    <line
                      x1={chartMargins.left}
                      y1={chartMargins.top + chart.plotHeight}
                      x2={chartMargins.left + chart.plotWidth}
                      y2={chartMargins.top + chart.plotHeight}
                      className="axis-line"
                    />
                    <line
                      x1={chartMargins.left}
                      y1={chartMargins.top}
                      x2={chartMargins.left}
                      y2={chartMargins.top + chart.plotHeight}
                      className="axis-line"
                    />

                    {chart.ticks.map((tick) => {
                      const y = chartMargins.top + chart.plotHeight - (tick / chart.chartMax) * chart.plotHeight;
                      return (
                        <g key={tick}>
                          <line
                            x1={chartMargins.left}
                            y1={y}
                            x2={chartMargins.left + chart.plotWidth}
                            y2={y}
                            className="grid-line"
                          />
                          <text x={chartMargins.left - 12} y={y + 4} textAnchor="end" className="axis-label">
                            {tick}
                          </text>
                        </g>
                      );
                    })}

                    <g>
                      <line
                        x1={chartMargins.left}
                        y1={chart.eightHourLineY}
                        x2={chartMargins.left + chart.plotWidth}
                        y2={chart.eightHourLineY}
                        className="reference-line"
                      />
                      <text
                        x={chartMargins.left + chart.plotWidth - 8}
                        y={chart.eightHourLineY - 6}
                        textAnchor="end"
                        className="reference-line-label"
                      >
                        8 hrs
                      </text>
                    </g>

                    <text
                      x={18}
                      y={chartMargins.top + chart.plotHeight / 2}
                      transform={`rotate(-90 18 ${chartMargins.top + chart.plotHeight / 2})`}
                      className="axis-title"
                      textAnchor="middle"
                    >
                      Estimated Hours
                    </text>

                    {chartData.dueDates.map((dueDate, dueDateIndex) => {
                      const groupX = chartMargins.left + dueDateIndex * chart.dateGroupWidth;
                      const barsStartX = groupX + (chart.dateGroupWidth - chart.actualGroupWidth) / 2;
                      const dateCenter = groupX + chart.dateGroupWidth / 2;
                      const dateSelected = selectedBar?.dueDateKey === dueDate.key || !selectedBar?.dueDateKey;

                      return (
                        <g key={dueDate.key}>
                          {chartData.managers.map((manager, managerIndex) => {
                            const value = dueDate.managers[manager] || 0;
                            if (!value) return null;

                            const x = barsStartX + managerIndex * (chart.barWidth + chart.barGap);
                            const barHeight = (value / chart.chartMax) * chart.plotHeight;
                            const y = chartMargins.top + chart.plotHeight - barHeight;
                            const isSelected = selectedBar?.dueDateKey === dueDate.key && selectedBar?.projectManager === manager;
                            const managerSelected = selectedBar?.projectManager === manager || !selectedBar?.projectManager;
                            const dimmed = selectedBar && !isSelected && !(dateSelected && managerSelected);

                            return (
                              <g key={`${dueDate.key}-${manager}`}>
                                <rect
                                  x={x}
                                  y={y}
                                  width={chart.barWidth}
                                  height={barHeight}
                                  fill={managerColorMap.get(manager)}
                                  className={`bar ${isSelected ? "selected" : ""} ${dimmed ? "dimmed" : ""}`}
                                  tabIndex="0"
                                  role="button"
                                  aria-label={`${dueDate.label} ${dueDate.year}, ${manager}, ${formatNumber(value)} estimated hours`}
                                  onClick={() => handleBarClick(dueDate.key, manager)}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                      event.preventDefault();
                                      handleBarClick(dueDate.key, manager);
                                    }
                                  }}
                                >
                                  <title>{`${dueDate.label} ${dueDate.year} - ${manager}: ${formatNumber(value)} hours`}</title>
                                </rect>
                                {barHeight > 18 && (
                                  <text
                                    x={x + chart.barWidth / 2}
                                    y={y - 6}
                                    textAnchor="middle"
                                    className={`bar-label ${dimmed ? "dimmed" : ""}`}
                                  >
                                    {formatNumber(value)}
                                  </text>
                                )}
                              </g>
                            );
                          })}

                          <text
                            x={dateCenter}
                            y={chartMargins.top + chart.plotHeight + 24}
                            textAnchor="middle"
                            className="date-label"
                          >
                            {dueDate.label}
                          </text>
                          <text
                            x={dateCenter}
                            y={chartMargins.top + chart.plotHeight + 48}
                            textAnchor="middle"
                            className="year-label"
                          >
                            {dueDate.year}
                          </text>
                        </g>
                      );
                    })}

                    <text
                      x={chartMargins.left + chart.plotWidth / 2}
                      y={chart.height - 10}
                      textAnchor="middle"
                      className="axis-title"
                    >
                      Due Date
                    </text>
                    </svg>
                  </div>
                </>
              )}
            </section>

            <div className="task-manager-pie-stack">
              <PieChart
                title="Estimated Hours by RSM"
                data={rsmPieData}
                selectedLabel={selectedRsm}
                onSelect={(label) => setSelectedRsm((current) => current === label ? null : label)}
              />
              <PieChart
                title="Estimated Hours by Project Manager"
                data={projectManagerPieData}
                selectedLabel={selectedBar?.projectManager || null}
                onSelect={(label) => {
                setSelectedBar((current) => (
                  current?.projectManager === label && !current?.dueDateKey
                    ? null
                    : { dueDateKey: null, projectManager: label }
                ));
              }}
              />
            </div>
          </div>

          <section className="task-manager-results-panel">
            <div className="task-manager-results-header">
              <div className="task-manager-chart-title">Results</div>
              <span>{filteredResultRows.length} items</span>
            </div>
            <div className="task-manager-results-table-wrap">
              <table className="task-manager-results-table">
                <thead>
                  <tr>
                    <th data-field="ProjectManager">Project Manager</th>
                    <th data-field="ProjectName">Project Name</th>
                    <th data-field="ProductCode">Product Code</th>
                    <th data-field="RequestType">Request Type</th>
                    <th data-field="RSM">RSM</th>
                    <th data-field="DueDate">Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResultRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-results">No matching activity log items.</td>
                    </tr>
                  ) : (
                    filteredResultRows.map((record) => {
                      const rowProjectManagerOptions = record.ProjectManagerValue
                        && !projectManagerOptionValues.includes(record.ProjectManagerValue)
                        ? [record.ProjectManagerValue, ...projectManagerOptionValues]
                        : projectManagerOptionValues;

                      return (
                        <tr key={record.ID}>
                          <td data-field="ProjectManager" data-label="Project Manager" className="task-manager-editable-cell">
                            <select
                              className="task-manager-manager-select"
                              value={record.ProjectManagerValue}
                              disabled={savingProjectManagerId === record.ID}
                              onChange={(event) => handleProjectManagerChange(record, event.target.value)}
                              aria-label={`Project Manager for ${record.ProjectName || record.ID}`}
                            >
                              <option value="">Select Project Manager</option>
                              {rowProjectManagerOptions.map((value) => (
                                <option key={value} value={value}>
                                  {value}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td data-field="ProjectName" data-label="Project Name">{record.ProjectName || ""}</td>
                          <td data-field="ProductCode" data-label="Product Code">{record.ProductCode || ""}</td>
                          <td data-field="RequestType" data-label="Request Type">{record.RequestType || ""}</td>
                          <td data-field="RSM" data-label="RSM">{record.RSM}</td>
                          <td data-field="DueDate" data-label="Due Date">{formatDateForTable(record.DueDate)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default TaskManagerDashboard;
